import { inject, Injectable } from '@angular/core';
import { CreateTagParams, InteractionTag, Tag, UpdateTagParams } from '../models/tag.model';
import { AuthService } from './auth.service';
import { ClockSyncService } from './clock-sync.service';
import { DatabaseService, type QuerySignal } from './database.service';

/**
 * The org's tag list (`config.manage-lists`) and the join table linking tags
 * to interactions. Tags are never hard-deleted — deactivate instead, per the
 * design doc §7 ("can be renamed and deactivated but not deleted while
 * referenced").
 */
@Injectable({
  providedIn: 'root',
})
export class TagService {
  private db = inject(DatabaseService);
  private clockSync = inject(ClockSyncService);
  private authService = inject(AuthService);

  /** Active, non-deleted tags in display order. */
  async getActive(): Promise<Tag[]> {
    await this.db.init();
    const rows = await this.db.exec(
      'SELECT * FROM tags WHERE is_active = 1 AND deleted_at IS NULL ORDER BY sort_order, label',
    );
    return rows as Tag[];
  }

  /** Every non-deleted tag, active or not — for admin management and historical display. */
  async getAll(): Promise<Tag[]> {
    await this.db.init();
    const rows = await this.db.exec(
      'SELECT * FROM tags WHERE deleted_at IS NULL ORDER BY sort_order, label',
    );
    return rows as Tag[];
  }

  createActiveQuery(): QuerySignal<Tag[]> {
    return this.db.createQuery('tags', () => this.getActive());
  }

  createQuery(): QuerySignal<Tag[]> {
    return this.db.createQuery('tags', () => this.getAll());
  }

  async create(params: CreateTagParams): Promise<Tag> {
    const userId = this.requireUser('create');
    await this.db.init();
    const id = this.db.generateId();
    const now = this.clockSync.now();

    await this.db.run(
      `INSERT INTO tags (id, label, color, category, sort_order, created_by, created_at, updated_by, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [id, params.label, params.color ?? 'primary', params.category ?? '', params.sort_order ?? 0, userId, now, userId, now],
    );

    const created = await this.getById(id);
    if (!created) throw new Error('Failed to retrieve tag after creation');
    return created;
  }

  async update(params: UpdateTagParams): Promise<Tag> {
    const userId = this.requireUser('update');
    await this.db.init();
    const existing = await this.getById(params.id);
    if (!existing) throw new Error(`Tag '${params.id}' not found`);

    const next: Tag = {
      ...existing,
      label: params.label ?? existing.label,
      color: params.color ?? existing.color,
      category: params.category ?? existing.category,
      sort_order: params.sort_order ?? existing.sort_order,
      is_active: params.is_active ?? existing.is_active,
    };

    await this.db.run(
      `UPDATE tags SET label = ?, color = ?, category = ?, sort_order = ?, is_active = ?, updated_by = ?, updated_at = ?
       WHERE id = ?`,
      [next.label, next.color, next.category, next.sort_order, next.is_active, userId, this.clockSync.now(), params.id],
    );

    return { ...next, updated_by: userId };
  }

  private async getById(id: string): Promise<Tag | null> {
    const rows = await this.db.exec('SELECT * FROM tags WHERE id = ? AND deleted_at IS NULL', [id]);
    return rows.length > 0 ? (rows[0] as Tag) : null;
  }

  /**
   * Reconciles an interaction's tag assignments to exactly `tagIds` —
   * inserting new join rows and soft-deleting removed ones. Mirrors
   * AttachmentService.saveFor()'s "extra join rows after entity save" shape.
   */
  async saveFor(interactionId: string, tagIds: string[]): Promise<void> {
    const userId = this.requireUser('tag');
    await this.db.init();
    const existing = (await this.db.exec(
      'SELECT * FROM interaction_tags WHERE interaction_id = ? AND deleted_at IS NULL',
      [interactionId],
    )) as InteractionTag[];

    const desired = new Set(tagIds);
    const now = this.clockSync.now();

    for (const row of existing) {
      if (!desired.has(row.tag_id)) {
        await this.db.run('UPDATE interaction_tags SET deleted_at = ? WHERE id = ?', [now, row.id]);
      }
    }

    const already = new Set(existing.filter((row) => desired.has(row.tag_id)).map((row) => row.tag_id));
    for (const tagId of desired) {
      if (already.has(tagId)) continue;
      await this.db.run(
        'INSERT INTO interaction_tags (id, interaction_id, tag_id, created_by, created_at) VALUES (?, ?, ?, ?, ?)',
        [this.db.generateId(), interactionId, tagId, userId, now],
      );
    }
  }

  /** Tag ids currently assigned to one interaction. */
  async getIdsFor(interactionId: string): Promise<string[]> {
    await this.db.init();
    const rows = (await this.db.exec(
      'SELECT tag_id FROM interaction_tags WHERE interaction_id = ? AND deleted_at IS NULL',
      [interactionId],
    )) as { tag_id: string }[];
    return rows.map((row) => row.tag_id);
  }

  /**
   * Resolved tags for many interactions at once, keyed by interaction id —
   * for timelines that render tag chips on every row.
   */
  async getForInteractions(interactionIds: string[]): Promise<Map<string, Tag[]>> {
    const result = new Map<string, Tag[]>();
    if (interactionIds.length === 0) return result;

    await this.db.init();
    const placeholders = interactionIds.map(() => '?').join(', ');
    const rows = (await this.db.exec(
      `SELECT it.interaction_id AS interaction_id, t.*
       FROM interaction_tags it
       JOIN tags t ON t.id = it.tag_id
       WHERE it.interaction_id IN (${placeholders}) AND it.deleted_at IS NULL AND t.deleted_at IS NULL
       ORDER BY t.sort_order, t.label`,
      interactionIds,
    )) as (Tag & { interaction_id: string })[];

    return this.groupByInteraction(rows);
  }

  createInteractionTagsQuery(interactionIds: string[]): QuerySignal<Map<string, Tag[]>> {
    return this.db.createQuery(
      'interaction_tags',
      () => this.getForInteractions(interactionIds),
      new Map<string, Tag[]>(),
    );
  }

  /**
   * Resolved tags for every interaction logged against one address, keyed by
   * interaction id — unlike {@link getForInteractions}, the id list is
   * derived from the join itself, so it stays correct as new interactions
   * are logged without the caller having to re-create the query.
   */
  async getForAddress(addressId: string): Promise<Map<string, Tag[]>> {
    await this.db.init();
    const rows = (await this.db.exec(
      `SELECT it.interaction_id AS interaction_id, t.*
       FROM interaction_tags it
       JOIN tags t ON t.id = it.tag_id
       JOIN interactions i ON i.id = it.interaction_id
       WHERE i.address_id = ? AND it.deleted_at IS NULL AND t.deleted_at IS NULL
       ORDER BY t.sort_order, t.label`,
      [addressId],
    )) as (Tag & { interaction_id: string })[];

    return this.groupByInteraction(rows);
  }

  createForAddressQuery(addressId: string): QuerySignal<Map<string, Tag[]>> {
    return this.db.createQuery('interaction_tags', () => this.getForAddress(addressId), new Map<string, Tag[]>());
  }

  /**
   * Reconciles a lead's tag assignments to exactly `tagIds` — same
   * diff-based join-row reconciliation as {@link saveFor}, over `lead_tags`
   * instead of `interaction_tags`.
   */
  async saveForLead(leadId: string, tagIds: string[]): Promise<void> {
    const userId = this.requireUser('tag');
    await this.db.init();
    const existing = (await this.db.exec(
      'SELECT * FROM lead_tags WHERE lead_id = ? AND deleted_at IS NULL',
      [leadId],
    )) as { id: string; tag_id: string }[];

    const desired = new Set(tagIds);
    const now = this.clockSync.now();

    for (const row of existing) {
      if (!desired.has(row.tag_id)) {
        await this.db.run('UPDATE lead_tags SET deleted_at = ? WHERE id = ?', [now, row.id]);
      }
    }

    const already = new Set(existing.filter((row) => desired.has(row.tag_id)).map((row) => row.tag_id));
    for (const tagId of desired) {
      if (already.has(tagId)) continue;
      await this.db.run(
        'INSERT INTO lead_tags (id, lead_id, tag_id, created_by, created_at) VALUES (?, ?, ?, ?, ?)',
        [this.db.generateId(), leadId, tagId, userId, now],
      );
    }
  }

  /** Tag ids currently assigned to one lead. */
  async getIdsForLead(leadId: string): Promise<string[]> {
    await this.db.init();
    const rows = (await this.db.exec(
      'SELECT tag_id FROM lead_tags WHERE lead_id = ? AND deleted_at IS NULL',
      [leadId],
    )) as { tag_id: string }[];
    return rows.map((row) => row.tag_id);
  }

  /** Resolved tags currently assigned to one lead, for the lead detail page. */
  async getForLead(leadId: string): Promise<Tag[]> {
    await this.db.init();
    const rows = (await this.db.exec(
      `SELECT t.* FROM lead_tags lt
       JOIN tags t ON t.id = lt.tag_id
       WHERE lt.lead_id = ? AND lt.deleted_at IS NULL AND t.deleted_at IS NULL
       ORDER BY t.sort_order, t.label`,
      [leadId],
    )) as Tag[];
    return rows;
  }

  createForLeadQuery(leadId: string): QuerySignal<Tag[]> {
    return this.db.createQuery('lead_tags', () => this.getForLead(leadId));
  }

  private groupByInteraction(rows: (Tag & { interaction_id: string })[]): Map<string, Tag[]> {
    const result = new Map<string, Tag[]>();
    for (const { interaction_id, ...tag } of rows) {
      const list = result.get(interaction_id) ?? [];
      list.push(tag as Tag);
      result.set(interaction_id, list);
    }
    return result;
  }

  private requireUser(action: string): string {
    const userId = this.authService.getCurrentUserId();
    if (!userId) throw new Error(`User must be authenticated to ${action} a tag`);
    return userId;
  }
}
